import type { NumericTextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import {
	NumericTextBox,
	TextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import { useContext, useMemo, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";

import { getEffectiveCarrierLiability } from "../../../carrier-selection/utils/carrier-liability.ts";
import { LoadValueModal } from "../load-value-modal/load-value-modal.tsx";

type CustomerReferencesSectionProps = {
	shipmentValueReadOnly?: boolean;
	/**
	 * Predicate used to surface validation errors (e.g. the required
	 * New/Used selection once a Shipment Value has been entered). Only the
	 * /ltl-shipment tender flow wires this up.
	 */
	hasFieldError?: (field: string) => boolean;
	/**
	 * When true, the Shipment Value and Product fields (and modal) are shown
	 * regardless of the `traxLtlShipmentValueModal` feature flag. Used by the
	 * /ltl-shipment page.
	 */
	ignoreShipmentValueFlag?: boolean;
	/**
	 * Clears a previously surfaced validation error for the given field.
	 */
	onClearFieldError?: (field: string) => void;
};

export function CustomerReferencesSection({
	shipmentValueReadOnly = false,
	hasFieldError,
	ignoreShipmentValueFlag = false,
	onClearFieldError,
}: Readonly<CustomerReferencesSectionProps> = {}) {
	const {
		isReady: shipmentValueFfIsReady,
		isEnabled: shipmentValueFfIsEnabled,
	} = useFeatureFlagGate("traxLtlShipmentValueModal");
	const { quoteState, onChange } = useContext(QuoteDetailsContext);

	const showShipmentValue =
		ignoreShipmentValueFlag ||
		(shipmentValueFfIsReady && shipmentValueFfIsEnabled);

	// A New/Used selection is only meaningful once a positive Shipment Value
	// has been entered. Until then the radios stay disabled, and once a value
	// exists the selection becomes required (validated by the tender flow).
	const hasShipmentValue =
		quoteState.insuranceValue != null && quoteState.insuranceValue > 0;

	const showProductRequiredError =
		hasShipmentValue && (hasFieldError?.("insuranceProductTypeId") ?? false);

	const handleShipmentValueChange = (e: NumericTextBoxChangeEvent) => {
		// Use null (not 0) when the field is cleared so it renders blank
		// rather than "$0.00".
		const nextValue = e.value ?? null;
		onChange("insuranceValue", nextValue);
		// Clearing the value also clears the New/Used selection so the two stay
		// in sync (a product can't persist without a value).
		if (
			(nextValue == null || nextValue <= 0) &&
			quoteState.insuranceProductTypeId != null
		) {
			onChange("insuranceProductTypeId", null);
		}
	};

	const handleProductChange = (productTypeId: number) => {
		onChange("insuranceProductTypeId", productTypeId);
		onClearFieldError?.("insuranceProductTypeId");
	};

	const selectedCarrierPrice = useMemo(
		() =>
			quoteState.carrierPrices.find(
				(carrierPrice) => carrierPrice.id === quoteState.selectedCarrierPriceId,
			),
		[quoteState.carrierPrices, quoteState.selectedCarrierPriceId],
	);

	const [showLoadValueModal, setShowLoadValueModal] = useState(false);
	const [isInsuranceAddedLocally, setIsInsuranceAddedLocally] = useState(false);

	const closeLoadValueModal = () => setShowLoadValueModal(false);

	const handleLoadValueBlur = () => {
		if (shipmentValueReadOnly) return;
		if (
			(quoteState.insuranceApplyTypeId == null || isInsuranceAddedLocally) &&
			quoteState.insuranceValue !== null &&
			quoteState.insuranceValue > 0 &&
			selectedCarrierPrice != null &&
			// Only surface the modal when the shipment value exceeds the carrier's
			// effective max liability. When the value is within (<=) liability the
			// shipment is already covered, so the add-insurance modal is not shown.
			quoteState.insuranceValue >
				getEffectiveCarrierLiability(
					selectedCarrierPrice,
					quoteState.insuranceProductTypeId,
				)
		) {
			setShowLoadValueModal(true);
		}
	};

	return (
		<>
			<div className={"additional-details-row"}>
				<div className={"additional-details-value"}>
					<Label editorId={"customerReference"}>Customer Reference</Label>
					<TextBox
						id={"customerReference"}
						name={`customerReference`}
						onChange={(e) => {
							onChange("customerReference", e.value as string);
						}}
						value={quoteState.customerReference || ""}
					/>
				</div>
				<div className={"additional-details-value"}>
					<Label editorId={"soNumber"}>SO Number</Label>
					<TextBox
						id={"soNumber"}
						name={`soNumber`}
						onChange={(e) => {
							onChange("soNumber", e.value as string);
						}}
						value={quoteState.soNumber || ""}
					/>
				</div>
				<div className={"additional-details-value"}>
					<Label editorId={"poNumbers"}>PO Number</Label>
					<TextBox
						id={"poNumbers"}
						name={`poNumbers`}
						onChange={(e) => {
							onChange("poNumbers", e.value as string);
						}}
						value={quoteState.poNumbers || ""}
					/>
				</div>
				{showShipmentValue ? (
					<div className={"additional-details-value"}>
						<Label editorId={"shipmentValue"}>Shipment Value</Label>
						{/*
						 * The Kendo NumericTextBox does not visually clear when its
						 * controlled `value` goes from a number to null programmatically
						 * (e.g. Clear Template or applying a template) — it keeps showing
						 * the last entered amount even though state is null. Keying the
						 * input on the applied templateId forces a fresh mount whenever the
						 * template context changes, so the field re-initializes empty.
						 * templateId is stable while typing, so this never disrupts input.
						 */}
						<NumericTextBox
							key={`shipment-value-${quoteState.templateId ?? "none"}`}
							format={"c2"}
							id={"shipmentValue"}
							min={0}
							name={`shipmentValue`}
							onBlur={handleLoadValueBlur}
							onChange={handleShipmentValueChange}
							readOnly={
								shipmentValueReadOnly ||
								(quoteState.insuranceApplyTypeId !== null &&
									!isInsuranceAddedLocally)
							}
							value={quoteState.insuranceValue}
						/>
					</div>
				) : null}
				{showShipmentValue ? (
					<div className={"additional-details-value"}>
						<Label>
							Product
							{hasShipmentValue ? (
								<span className={"required"}>&nbsp;*</span>
							) : null}
						</Label>
						<div className={"product-radio"}>
							<div>
								<RadioButton
									checked={quoteState.insuranceProductTypeId === 1}
									disabled={shipmentValueReadOnly || !hasShipmentValue}
									label={"New"}
									name={"product"}
									onChange={() => handleProductChange(1)}
									value={"new"}
								/>
							</div>
							<div>
								<RadioButton
									checked={quoteState.insuranceProductTypeId === 2}
									disabled={shipmentValueReadOnly || !hasShipmentValue}
									label={"Used"}
									name={"product"}
									onChange={() => handleProductChange(2)}
									value={"used"}
								/>
							</div>
						</div>
						{showProductRequiredError ? <Error>Required</Error> : null}
					</div>
				) : null}
			</div>

			{showLoadValueModal && showShipmentValue ? (
				<LoadValueModal
					onClose={closeLoadValueModal}
					onInsuranceAdded={() => setIsInsuranceAddedLocally(true)}
					quoteId={quoteState.id}
					selectedCarrierPrice={selectedCarrierPrice}
					shipmentValue={quoteState.insuranceValue}
				/>
			) : null}
		</>
	);
}
